import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import type { SimpleMdmInstall } from "@meridian-mdm/shared";
import { fetchAppDetail, createJob, updateLatestVersion } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import {
  ArrowLeft,
  ArrowUpDown,
  Rocket,
  Search,
  AlertTriangle,
  Pencil,
  Check,
  X,
} from "lucide-react";

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diffSec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86_400) return `${Math.round(diffSec / 3600)}h ago`;
  const days = Math.round(diffSec / 86_400);
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

function isStale(iso: string | null | undefined): boolean {
  if (!iso) return true;
  return Date.now() - new Date(iso).getTime() > 7 * 86_400_000;
}

const columnHelper = createColumnHelper<SimpleMdmInstall>();

export default function AppDetail() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const numericAppId = Number(appId);

  const [globalFilter, setGlobalFilter] = useState("");
  const [hideUnenrolled, setHideUnenrolled] = useState(true);
  const [onlyOutOfDate, setOnlyOutOfDate] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([{ id: "deviceName", desc: false }]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [targetVersionOverride, setTargetVersionOverride] = useState("");
  const [editingLatest, setEditingLatest] = useState(false);
  const [latestDraft, setLatestDraft] = useState("");

  const {
    data: detail,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["appDetail", numericAppId],
    queryFn: () => fetchAppDetail(numericAppId),
    enabled: !!numericAppId,
  });

  const latestVersion = detail?.latestVersion?.version ?? null;
  const bundleId = detail?.app.bundle_identifier;

  const overrideMutation = useMutation({
    mutationFn: (version: string) => updateLatestVersion(bundleId!, version),
    onSuccess: () => {
      toast({ title: "Latest version updated", description: latestDraft });
      setEditingLatest(false);
      setLatestDraft("");
      void queryClient.invalidateQueries({ queryKey: ["appDetail", numericAppId] });
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const createJobMutation = useMutation({
    mutationFn: () =>
      createJob({
        app_id: numericAppId,
        target_version: targetVersionOverride || latestVersion || "",
      }),
    onSuccess: (data) => {
      setDialogOpen(false);
      setTargetVersionOverride("");
      toast({
        title: "Job created",
        description: `Update job #${data.jobId} has been started.`,
      });
      void navigate(`/jobs/${data.jobId}`);
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Failed to create job",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.device.name, {
        id: "deviceName",
        header: "Device",
        cell: (info) => info.getValue() || "—",
      }),
      columnHelper.accessor((row) => row.device.serial_number, {
        id: "serial",
        header: "Serial",
        cell: (info) => <span className="font-mono text-xs">{info.getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.device.os_version, {
        id: "os",
        header: "iOS",
        cell: (info) => <span className="font-mono text-xs">{info.getValue() ?? "—"}</span>,
      }),
      columnHelper.accessor((row) => row.device.model_name, {
        id: "model",
        header: "Model",
        cell: (info) => info.getValue() ?? "—",
      }),
      columnHelper.accessor("version", {
        header: "Installed Version",
        cell: (info) => {
          const v = info.getValue();
          const outOfDate = latestVersion && v !== latestVersion;
          return (
            <span
              className={
                outOfDate
                  ? "font-mono text-sm text-amber-700 dark:text-amber-400"
                  : "font-mono text-sm"
              }
            >
              {v}
              {outOfDate && (
                <AlertTriangle className="ml-1 inline h-3 w-3" aria-label="out of date" />
              )}
            </span>
          );
        },
      }),
      columnHelper.accessor((row) => row.device.last_seen_at, {
        id: "lastSeen",
        header: "Last Seen",
        cell: (info) => {
          const iso = info.getValue();
          return (
            <span
              className={isStale(iso) ? "text-xs text-muted-foreground" : "text-xs"}
              title={iso ?? ""}
            >
              {timeAgo(iso)}
            </span>
          );
        },
        sortingFn: (a, b) => {
          const av = a.original.device.last_seen_at;
          const bv = b.original.device.last_seen_at;
          return new Date(av ?? 0).getTime() - new Date(bv ?? 0).getTime();
        },
      }),
      columnHelper.accessor((row) => row.device.status, {
        id: "status",
        header: "Status",
        cell: (info) => {
          const s = info.getValue();
          const variant =
            s === "enrolled"
              ? "border-green-300 bg-green-100 text-green-700"
              : s === "unenrolled"
                ? "border-red-300 bg-red-100 text-red-700"
                : "border-gray-300 bg-gray-100 text-gray-700";
          return (
            <Badge variant="outline" className={variant}>
              {s ?? "unknown"}
            </Badge>
          );
        },
      }),
    ],
    [latestVersion],
  );

  const filteredInstalls = useMemo(() => {
    const base = detail?.installs ?? [];
    return base.filter((i) => {
      if (hideUnenrolled && i.device.status === "unenrolled") return false;
      if (onlyOutOfDate && latestVersion && i.version === latestVersion) return false;
      return true;
    });
  }, [detail?.installs, hideUnenrolled, onlyOutOfDate, latestVersion]);

  const table = useReactTable({
    data: filteredInstalls,
    columns,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: (row, _columnId, filterValue: string) => {
      const q = filterValue.toLowerCase();
      const d = row.original.device;
      return (
        (d.name ?? "").toLowerCase().includes(q) ||
        (d.serial_number ?? "").toLowerCase().includes(q) ||
        (d.model_name ?? "").toLowerCase().includes(q) ||
        row.original.version.toLowerCase().includes(q)
      );
    },
  });

  const versionDistribution = useMemo(() => {
    const counts = new Map<string, number>();
    for (const i of detail?.installs ?? []) {
      counts.set(i.version, (counts.get(i.version) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [detail?.installs]);

  const outOfDateCount = useMemo(() => {
    if (!latestVersion) return 0;
    return (detail?.installs ?? []).filter(
      (i) => i.version !== latestVersion && i.device.status !== "unenrolled",
    ).length;
  }, [detail?.installs, latestVersion]);

  const app = detail?.app;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => void navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{app?.name ?? `App ${appId}`}</h1>
          {app && (
            <p className="font-mono text-sm text-muted-foreground">{app.bundle_identifier}</p>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load app detail: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}

      {app && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-medium">App Info</CardTitle>
            <CardDescription>
              Current App Store version drives the &quot;out of date&quot; flag below. Latest
              version auto-syncs on page load (cached 1 hour).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-4">
              <InfoField label="Platform">{app.platform_support ?? "—"}</InfoField>
              <InfoField label="iTunes Store ID">
                <span className="font-mono">{app.itunes_store_id ?? "—"}</span>
              </InfoField>
              <InfoField label="Latest Version">
                {editingLatest ? (
                  <span className="flex items-center gap-1">
                    <Input
                      autoFocus
                      value={latestDraft}
                      onChange={(e) => setLatestDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && latestDraft.trim() && bundleId) {
                          overrideMutation.mutate(latestDraft.trim());
                        } else if (e.key === "Escape") {
                          setEditingLatest(false);
                          setLatestDraft("");
                        }
                      }}
                      placeholder="e.g. 20260113.23"
                      className="h-7 w-36 font-mono text-sm"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!latestDraft.trim() || overrideMutation.isPending}
                      onClick={() => overrideMutation.mutate(latestDraft.trim())}
                      title="Save"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        setEditingLatest(false);
                        setLatestDraft("");
                      }}
                      title="Cancel"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    {latestVersion ? (
                      <span className="font-mono text-base font-semibold">{latestVersion}</span>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-400">Not set</span>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setLatestDraft(latestVersion ?? "");
                        setEditingLatest(true);
                      }}
                      title="Override manually (stops auto-sync)"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </span>
                )}
              </InfoField>
              <InfoField label="Source">
                {detail?.latestVersion ? (
                  <span className="space-x-1">
                    <Badge variant="outline" className="text-xs">
                      {detail.latestVersion.source === "asc"
                        ? "App Store Connect"
                        : detail.latestVersion.source === "itunes"
                          ? "App Store"
                          : "Manual"}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      synced {timeAgo(detail.latestVersion.updatedAt)}
                    </span>
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </InfoField>
            </div>

            {versionDistribution.length > 0 && (
              <div>
                <div className="mb-1 text-sm font-medium">Version distribution</div>
                <div className="flex flex-wrap gap-2">
                  {versionDistribution.map(([v, n]) => (
                    <Badge
                      key={v}
                      variant="outline"
                      className={
                        v === latestVersion
                          ? "border-green-300 bg-green-50 font-mono text-xs text-green-700"
                          : "font-mono text-xs"
                      }
                      title={v === latestVersion ? "latest" : "out of date"}
                    >
                      {v} · {n}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-medium">
            Installed Devices ({detail?.installs?.length ?? 0})
          </CardTitle>
          <Button
            onClick={() => setDialogOpen(true)}
            disabled={!detail?.installs?.length || !latestVersion || outOfDateCount === 0}
            className="gap-2"
            title={
              !latestVersion
                ? "Set a latest version first"
                : outOfDateCount === 0
                  ? "All devices are already on the latest version"
                  : undefined
            }
          >
            <Rocket className="h-4 w-4" />
            Update {outOfDateCount} Out-of-Date
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, serial, model, version…"
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-9"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={hideUnenrolled}
                onChange={(e) => setHideUnenrolled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Hide unenrolled
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyOutOfDate}
                onChange={(e) => setOnlyOutOfDate(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
                disabled={!latestVersion}
              />
              Only out of date
            </label>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : (
                          <button
                            type="button"
                            className={
                              header.column.getCanSort()
                                ? "flex items-center gap-1 hover:text-foreground"
                                : ""
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getCanSort() && (
                              <ArrowUpDown
                                className={`h-3 w-3 ${
                                  header.column.getIsSorted()
                                    ? "text-foreground"
                                    : "text-muted-foreground/50"
                                }`}
                              />
                            )}
                          </button>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center text-muted-foreground"
                    >
                      No devices match current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update {outOfDateCount} Out-of-Date Devices</DialogTitle>
            <DialogDescription>
              Target version: <span className="font-mono font-semibold">{latestVersion}</span>
              {detail?.latestVersion?.source === "asc" && (
                <span className="text-xs text-muted-foreground"> (from App Store Connect)</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm">
              SimpleMDM will push the latest App Store version of {app?.name} to {outOfDateCount}{" "}
              device{outOfDateCount === 1 ? "" : "s"}. Devices already on the target version are
              skipped automatically.
            </p>
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Need to pin a different version?</summary>
              <div className="mt-2 space-y-1">
                <Label htmlFor="targetOverride">Override target version</Label>
                <Input
                  id="targetOverride"
                  placeholder={latestVersion ?? ""}
                  value={targetVersionOverride}
                  onChange={(e) => setTargetVersionOverride(e.target.value)}
                />
              </div>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createJobMutation.mutate()}
              disabled={createJobMutation.isPending || !latestVersion}
            >
              {createJobMutation.isPending ? "Starting…" : "Start Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}
